import { checkPermissions } from "@elit-hair/elit-tools";
import { Badge, Button, CircularProgress, Divider, IconButton, Popover } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbBell, TbLayoutList } from "react-icons/tb";
import { Link, NavLink, useLocation } from "react-router-dom";

import UserDropdown from "@/components/UserDropdown";
import GlobalSearch from "@/components/globalSearch";
import routes from "@/constants/routes";
import { useCapacitor } from "@/hooks/useCapacitor";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import NotificationList from "@/pages/Notifications/components/list";
import NotificationHeader from "@/pages/Notifications/components/list/NotificationHeader";
import useAuth from "@/providers/hooks/useAuth";
import { useGetUnreadCount } from "@/requests/notification.queries";

const Navbar = () => {
    const location = useLocation();

    const { t } = useTranslation();

    const { userData } = useAuth();

    return (
        <>
            {/* Desktop Navbar */}
            <div className={`z-50 hidden md:block sticky top-0`}>
                <div className="w-full h-12 flex items-center bg-elitDarkBlue shadow-lg shadow-white/10 text-white rounded-none overflow-hidden">
                    <div className="flex justify-around items-center px-4 w-full h-full">
                        {routes.map((route) => {
                            if (!route.icon || route.hidden) return null;

                            if (!checkPermissions(userData?.permissionCodes, route.permissions)) return null;
                            if (route.path === "/notifications") return null;

                            const isCurrent = location.pathname === route.path;

                            return (
                                <Link
                                    draggable={false}
                                    className={`select-none transition-opacity no-underline text-white h-full w-full flex justify-center items-center ${
                                        isCurrent ? "opacity-100" : "opacity-50 hover:opacity-75"
                                    }`}
                                    to={route.path}
                                    key={route.path}
                                >
                                    <div className="flex justify-center items-center md:flex-row flex-col">
                                        <route.icon />
                                        {route.title && (
                                            <div
                                                className={`text-xs md:text-base font-semibold rounded-lg px-2 text-nowrap ${
                                                    isCurrent ? "bg-elitOrange ml-2" : ""
                                                }`}
                                            >
                                                {t(route.title || "")}
                                            </div>
                                        )}
                                    </div>
                                </Link>
                            );
                        })}
                        <GlobalSearch />
                        <NotificationItem />
                        <UserDropdown />
                    </div>
                </div>
            </div>
        </>
    );
};

function NotificationItem() {
    const { data, isLoading: loadingUnread } = useGetUnreadCount();
    const [showNotifications, setShowNotifications] = useState<HTMLElement | null>(null);
    const { size } = useWindowDimensions();

    const unreadCount = useMemo(() => data?.count || 0, [data]);
    const { isCordova } = useCapacitor();
    const isMobile = !size.md || isCordova;

    return (
        (!isMobile && (
            <>
                <IconButton
                    id="notifications"
                    color="inherit"
                    className="!shrink-0 !grow-0"
                    onClick={(event) => setShowNotifications(event.currentTarget)}
                >
                    <Badge
                        classes={{
                            badge: "!animate-pulse  bg-elitOrange",
                        }}
                        badgeContent={
                            loadingUnread ? (
                                <CircularProgress color="inherit" size={"10px"} />
                            ) : unreadCount > 0 ? (
                                unreadCount
                            ) : undefined
                        }
                    >
                        <TbBell className="opacity-50 hover:opacity-75" />
                    </Badge>
                </IconButton>
                <Popover
                    onClose={() => setShowNotifications(null)}
                    open={Boolean(showNotifications)}
                    anchorEl={document.querySelector("#notifications")}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                borderRadius: 3,
                            },
                        },
                    }}
                >
                    <Notifications handleClose={() => setShowNotifications(null)} />
                </Popover>
            </>
        )) ||
        null
    );
}

function Notifications({ handleClose }: { handleClose: () => void }) {
    return (
        <>
            <NotificationList maxHeight={"400px"} className="w-[720px]" handleClose={handleClose}>
                <NotificationHeader />
            </NotificationList>
            <Divider className="!bg-gray-200  !w-[75%] !mx-auto" />
            <div className="flex justify-end p-2">
                <Button
                    component={NavLink}
                    to={"/notifications"}
                    startIcon={<TbLayoutList />}
                    className=""
                    type="submit"
                    onClick={() => handleClose()}
                    variant="outlined"
                    color="primary"
                >
                    Tüm Bildirimleri Gör
                </Button>
            </div>
        </>
    );
}
export default Navbar;
